import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ClientProfile } from '../../models/client/client-profile.model';
import { BaseService } from '../base/base.service';
import { ChangePassword } from '../../models/client/change-password.model';
import { Email } from '../../models/client/email.model';
import { PhoneNumber } from '../../models/client/phone-number.model';
import { VerifyEmail } from '../../models/client/verify-email.model';
import {
  NewPhoneNumber,
  VerifyPhoneNumber
} from '../../models/client/verify-phone-number.model';

@Injectable({
  providedIn: 'root'
})
export class ClientProfileService extends BaseService<ClientProfile> {
  protected baseUrl = environment.api_url;
  protected resourceUrl = `${environment.api_url}/client-profile`;
  clientDetails = new BehaviorSubject<ClientProfile>(null);

  constructor(protected http: HttpClient) {
    super(http, 'client-profile');
  }

  changeLanguage(language: string): Observable<HttpResponse<null>> {
    return this.http.patch<null>(
      `${this.resourceUrl}/language`,
      { language },
      {
        observe: 'response'
      }
    );
  }

  changePassword(body: ChangePassword): Observable<HttpResponse<null>> {
    return this.http.post<null>(`${this.resourceUrl}/change-password`, body, {
      observe: 'response'
    });
  }

  updateClientProfile(
    formData: FormData
  ): Observable<HttpResponse<ClientProfile>> {
    return this.http.post<ClientProfile>(`${this.resourceUrl}`, formData, {
      observe: 'response'
    });
  }

  getPhoneCode(phoneNumber: string): Observable<HttpResponse<PhoneNumber>> {
    return this.http.get<PhoneNumber>(
      `${this.baseUrl}/clients/two-factor/verify/phone?phoneNumber=${phoneNumber}`,
      {
        observe: 'response'
      }
    );
  }

  sendPhoneCode(phoneNumber: string): Observable<HttpResponse<null>> {
    return this.http.post<null>(
      `${this.baseUrl}/clients/two-factor/send/code?phoneNumber=${phoneNumber}`,
      null,
      {
        observe: 'response'
      }
    );
  }

  initPhoneChange(body: NewPhoneNumber): Observable<HttpResponse<null>> {
    return this.http.post<null>(
      `${this.baseUrl}/clients/two-factor/change/phone/init`,
      body,
      {
        observe: 'response'
      }
    );
  }

  newPhoneNumber(body: NewPhoneNumber): Observable<HttpResponse<PhoneNumber>> {
    return this.http.post<PhoneNumber>(
      `${this.baseUrl}/clients/two-factor/change/phone/code`,
      body,
      {
        observe: 'response'
      }
    );
  }

  verifyPhoneNumber(
    body: VerifyPhoneNumber
  ): Observable<HttpResponse<ClientProfile>> {
    return this.http.post<ClientProfile>(
      `${this.baseUrl}/clients/two-factor/verify/phone/code`,
      body,
      {
        observe: 'response'
      }
    );
  }

  changeEmail(email: string): Observable<HttpResponse<Email>> {
    return this.http.post<Email>(
      `${this.resourceUrl}/change-email`,
      { email },
      {
        observe: 'response'
      }
    );
  }

  verifyCurrentEmail(
    body: VerifyEmail,
    verificationHash: string
  ): Observable<HttpResponse<null>> {
    return this.http.post<null>(
      `${this.resourceUrl}/change-email/verify-current`,
      body,
      {
        headers: { 'X-Verification-Hash': verificationHash },
        observe: 'response'
      }
    );
  }

  verifyEmail(
    body: VerifyEmail,
    verificationHash: string
  ): Observable<HttpResponse<ClientProfile>> {
    return this.http.post<ClientProfile>(
      `${this.resourceUrl}/verify-email`,
      body,
      {
        headers: { 'X-Verification-Hash': verificationHash },
        observe: 'response'
      }
    );
  }

  setPassword(password: string): Observable<HttpResponse<null>> {
    return this.http.post<null>(
      `${this.resourceUrl}/set-password`,
      { password },
      {
        observe: 'response'
      }
    );
  }

  changeWalletAddress(walletAddress: string): Observable<HttpResponse<null>> {
    return this.http.post<null>(
      `${this.resourceUrl}/change-wallet-address`,
      { walletAddress },
      { observe: 'response' }
    );
  }

  verifyWalletAddress(
    body: { walletAddress: string; verificationCode: string },
    verificationHash: string
  ): Observable<HttpResponse<{ walletAddress: string }>> {
    return this.http.post<{ walletAddress: string }>(
      `${this.resourceUrl}/verify-wallet-address`,
      body,
      {
        headers: { 'X-Verification-Hash': verificationHash },
        observe: 'response'
      }
    );
  }

  deleteUser(): Observable<HttpResponse<null>> {
    return this.http.request<null>('delete', `${this.baseUrl}/clients/delete`, {
      observe: 'response'
    });
  }
}
